import { useState, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { MessageCircle, X, Send, Loader2, Zap, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api, { formatApiError } from "@/lib/api";

const STORAGE_KEY = "rp_chat_session_id";
const MSG_KEY = "rp_chat_messages";

const SUGGESTIONS = [
  "How do I generate a blog post?",
  "What is the autopilot pipeline?",
  "How does the SEO audit work?",
  "Can I post to Telegram automatically?",
];

const HIDDEN_PATHS = ["/login", "/register", "/auth/callback", "/forgot-password", "/reset-password"];
const HIDDEN_PREFIXES = ["/p/"];

function FormattedReply({ text }) {
  // Lightweight markdown-ish renderer: **bold**, line breaks, simple bullets
  const lines = text.split("\n");
  return (
    <div className="space-y-1.5">
      {lines.map((line, i) => {
        const trimmed = line.trim();
        if (!trimmed) return <div key={i} className="h-2" />;
        const isBullet = /^[-•*]\s/.test(trimmed);
        const content = (isBullet ? trimmed.replace(/^[-•*]\s/, "") : trimmed)
          .split(/(\*\*[^*]+\*\*)/g)
          .map((part, idx) =>
            part.startsWith("**") && part.endsWith("**") ? (
              <strong key={idx} className="text-zinc-100">{part.slice(2, -2)}</strong>
            ) : (
              <span key={idx}>{part}</span>
            )
          );
        return (
          <div key={i} className={isBullet ? "flex gap-2" : ""}>
            {isBullet && <span className="text-[#3377FF] mt-0.5">•</span>}
            <span className="flex-1">{content}</span>
          </div>
        );
      })}
    </div>
  );
}

export default function ChatbotWidget() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(() => {
    try { return JSON.parse(localStorage.getItem(MSG_KEY) || "[]"); } catch { return []; }
  });
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(() => localStorage.getItem(STORAGE_KEY) || "");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Hide on auth pages
  const hidden = HIDDEN_PATHS.some(p => location.pathname === p || location.pathname.startsWith(p + "/"))
    || HIDDEN_PREFIXES.some(p => location.pathname.startsWith(p));

  useEffect(() => {
    if (open) {
      setTimeout(() => {
        scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
        inputRef.current?.focus();
      }, 50);
    }
  }, [open, messages]);

  useEffect(() => {
    try { localStorage.setItem(MSG_KEY, JSON.stringify(messages.slice(-30))); } catch {}
  }, [messages]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: msg }]);
    setLoading(true);
    try {
      const { data } = await api.post("/chatbot/message", { message: msg, session_id: sessionId || undefined });
      if (data.session_id && data.session_id !== sessionId) {
        setSessionId(data.session_id);
        localStorage.setItem(STORAGE_KEY, data.session_id);
      }
      setMessages(prev => [...prev, { role: "assistant", content: data.reply }]);
    } catch (e) {
      setMessages(prev => [...prev, { role: "assistant", content: `Sorry, I hit an error: ${formatApiError(e)}` }]);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setMessages([]);
    setSessionId("");
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(MSG_KEY);
  };

  if (hidden) return null;

  return (
    <>
      {/* Floating launcher */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          aria-label="Open RankPilot helper"
          data-testid="chatbot-launcher"
          className="fixed bottom-6 right-6 z-40 group"
        >
          <span className="absolute inset-0 rounded-full bg-[#0055FF]/40 blur-xl group-hover:bg-[#0055FF]/60 transition-all" />
          <span className="relative flex items-center gap-2 bg-[#0055FF] hover:bg-[#3377FF] text-white pl-4 pr-5 py-3.5 rounded-full shadow-lg shadow-[#0055FF]/30 transition-all hover:scale-105">
            <MessageCircle className="w-5 h-5" />
            <span className="text-sm font-medium hidden sm:inline">Ask RankPilot</span>
          </span>
        </button>
      )}

      {/* Panel */}
      {open && (
        <div
          className="fixed bottom-6 right-6 z-40 w-[calc(100vw-3rem)] sm:w-[400px] h-[600px] max-h-[calc(100vh-3rem)] flex flex-col bg-[#0B0B0E] border border-zinc-800 rounded-sm shadow-2xl shadow-black/60 overflow-hidden"
          data-testid="chatbot-panel"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800 bg-gradient-to-r from-[#0055FF]/15 to-transparent">
            <div className="flex items-center gap-2.5">
              <div className="relative">
                <div className="w-8 h-8 rounded-sm bg-[#0055FF] grid place-items-center">
                  <Zap className="w-4 h-4 text-white" />
                </div>
                <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-[#10B981] border-2 border-[#0B0B0E]" />
              </div>
              <div>
                <div className="font-display text-sm font-semibold leading-tight">RankPilot helper</div>
                <div className="text-[10px] tracking-[0.18em] uppercase text-zinc-500 leading-tight mt-0.5">GPT-5.2 · Online</div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {messages.length > 0 && (
                <button onClick={reset} className="text-xs text-zinc-500 hover:text-zinc-200 transition-colors px-2 py-1" data-testid="chatbot-reset">
                  Reset
                </button>
              )}
              <button onClick={() => setOpen(false)} aria-label="Close chat" data-testid="chatbot-close" className="w-8 h-8 grid place-items-center text-zinc-500 hover:text-zinc-200 transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Body */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-4" data-testid="chatbot-messages">
            {messages.length === 0 && (
              <div className="space-y-4">
                <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="w-4 h-4 text-[#3377FF]" />
                    <span className="text-sm font-medium">Hey there 👋</span>
                  </div>
                  <p className="text-sm text-zinc-400 leading-relaxed">
                    I'm your RankPilot helper. Ask me how to use any feature — audits, blog generation, autopilot, scheduling, social posting, anything.
                  </p>
                </div>
                <div className="space-y-2">
                  <div className="text-[10px] tracking-[0.18em] uppercase text-zinc-600 px-1">Try asking</div>
                  {SUGGESTIONS.map((s, i) => (
                    <button
                      key={i}
                      onClick={() => send(s)}
                      className="w-full text-left text-sm bg-zinc-950 hover:bg-zinc-900 border border-zinc-800 hover:border-zinc-700 rounded-sm px-3 py-2.5 text-zinc-300 transition-colors"
                      data-testid={`chatbot-suggest-${i}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] rounded-sm px-3.5 py-2.5 text-sm leading-relaxed ${
                    m.role === "user"
                      ? "bg-[#0055FF] text-white"
                      : "bg-zinc-950 border border-zinc-800 text-zinc-200"
                  }`}
                  data-testid={`chatbot-msg-${m.role}-${i}`}
                >
                  {m.role === "assistant" ? <FormattedReply text={m.content} /> : m.content}
                </div>
              </div>
            ))}

            {loading && (
              <div className="flex justify-start" data-testid="chatbot-typing">
                <div className="bg-zinc-950 border border-zinc-800 rounded-sm px-3.5 py-3 inline-flex gap-1.5">
                  <span className="w-1.5 h-1.5 bg-zinc-500 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                  <span className="w-1.5 h-1.5 bg-zinc-500 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                  <span className="w-1.5 h-1.5 bg-zinc-500 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="border-t border-zinc-800 p-3 flex gap-2 bg-zinc-950/40"
            data-testid="chatbot-form"
          >
            <Input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask anything about RankPilot…"
              disabled={loading}
              className="h-10 bg-zinc-950 border-zinc-800 rounded-sm focus-visible:ring-[#0055FF] text-sm"
              data-testid="chatbot-input"
            />
            <Button
              type="submit"
              disabled={loading || !input.trim()}
              className="h-10 w-10 p-0 bg-[#0055FF] hover:bg-[#3377FF] rounded-sm"
              data-testid="chatbot-send"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </form>
        </div>
      )}
    </>
  );
}
